import { Box, Button, IconButton, Paper, Stack, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DesktopWindowsOutlinedIcon from '@mui/icons-material/DesktopWindowsOutlined';
import { useDesktopAppPromo } from '@/hooks/useDesktopAppPromo';
import { RouteMap } from '@/routes/Routes';

interface DesktopAppPromoProps {
  /** True while the owner is waiting on their own note to finish processing. */
  eligible: boolean;
}

/**
 * Suggests the desktop app in the wait right after a recording, when the
 * person is watching the note process. Renders nothing unless
 * `useDesktopAppPromo` is sure they don't already have the app.
 */
const DesktopAppPromo = ({ eligible }: DesktopAppPromoProps) => {
  const { os, dismiss } = useDesktopAppPromo(eligible);

  if (!os) return null;

  const osName = os === 'mac' ? 'Mac' : 'Windows';

  return (
    <Paper
      elevation={0}
      role="region"
      aria-labelledby="desktop-app-promo-title"
      sx={{
        mt: 2,
        p: 2,
        maxWidth: '800px',
        backgroundColor: 'grey.50',
        border: '1px solid',
        borderColor: 'grey.200',
        borderRadius: 2,
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <Box
          sx={{
            width: 36,
            height: 36,
            flexShrink: 0,
            borderRadius: '50%',
            backgroundColor: 'primary.light',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <DesktopWindowsOutlinedIcon sx={{ fontSize: 20, color: 'primary.main' }} />
        </Box>
        <Stack spacing={1} flex={1}>
          <Typography id="desktop-app-promo-title" variant="body1" fontWeight={700}>
            Wish you could trim the beginning of this recording, or record in 1080p?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Open your notes in the free NoteGo desktop app for {osName} — trim recordings, record in 1080p, and get
            titles right away.
          </Typography>
          <Box>
            {/* New tab, so the note keeps processing in this one. */}
            <Button
              variant="contained"
              color="primary"
              size="small"
              href={RouteMap.SETTINGS_DESKTOP_APP}
              target="_blank"
              rel="noopener noreferrer"
              onClick={dismiss}
            >
              Get the {osName} app
            </Button>
          </Box>
        </Stack>
        <IconButton size="small" onClick={dismiss} aria-label="Dismiss desktop app suggestion">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Stack>
    </Paper>
  );
};

export default DesktopAppPromo;
